import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

type DueActivity = {
  id: string;
  type: string;
  body?: string | null;
  dueAt?: string | null;
  dealName?: string | null;
};

export function useActivityNotifications(enabled: boolean = true) {
  const { toast } = useToast();
  const notifiedIds = useRef<Set<string>>(new Set());

  const { data: dueActivitiesData } = useQuery<DueActivity[]>({
    queryKey: ["/api/crm/activities/due"],
    enabled,
    refetchInterval: 60000,
  });
  const dueActivities = dueActivitiesData ?? [];

  useEffect(() => {
    if (dueActivities.length === 0) return;

    dueActivities.forEach(activity => {
      if (notifiedIds.current.has(activity.id)) return;
      notifiedIds.current.add(activity.id);

      const typeLabels: Record<string, string> = {
        task: "Task",
        call: "Call",
        email: "Email",
        sms: "SMS",
        meeting: "Meeting",
        note: "Note",
      };
      const typeLabel = typeLabels[activity.type] || "Activity";
      const desc = activity.body ? activity.body.slice(0, 80) : (activity.dealName ? `Deal: ${activity.dealName}` : "");

      toast({
        title: `${typeLabel} is due`,
        description: desc || "You have an overdue activity",
        variant: "destructive",
      });
    });
  }, [dueActivities, toast]);

  return { dueCount: dueActivities.length };
}
